import { Version } from "Infrastructure/Api/Api";
import { AccessRoleCodes } from "Models/AccessRoleCodes";

/**
 * Whether a persona of this role may follow other people. Mirrors the backend's
 * SocialConstants.CanFollow: only Customer personas may; business personas (Merchant, Hunter,
 * HunterPlus) exist to be followed, and back-office personas take no part in social features.
 * Every frontend surface that hides or redirects on this rule reads it from here.
 */
export const canRoleFollow = (roleCode: string | null | undefined) =>
  roleCode === AccessRoleCodes.Customer;

/**
 * Whether a persona of this role can be followed. Mirrors the backend's
 * SocialConstants.CanBeFollowed (Rules v2, K2/K3): Customers and Merchants can, Hunters can't.
 */
export const canRoleBeFollowed = (roleCode: string | null | undefined) =>
  roleCode === AccessRoleCodes.Customer ||
  roleCode === AccessRoleCodes.Merchant;

/**
 * Whether a persona of the viewer's role may START contact with a persona of the target's role —
 * create a chat with them or add them to a group. Mirrors the backend's
 * SocialConstants.CanInitiateChatWith (App ADR-0015): a Merchant, Hunter or HunterPlus never brings
 * a Customer into a conversation, the Customer has to reach out first. An unknown target role
 * passes — the server decides.
 */
export const canRoleInitiateChatWith = (
  viewerRoleCode: string | null | undefined,
  targetRoleCode: string | null | undefined,
) =>
  targetRoleCode !== AccessRoleCodes.Customer ||
  (viewerRoleCode !== AccessRoleCodes.Merchant &&
    viewerRoleCode !== AccessRoleCodes.Hunter &&
    viewerRoleCode !== AccessRoleCodes.HunterPlus);

/**
 * Whether a persona counts as Full Moone (completed KYC registration). Mirrors the backend's
 * SocialConstants.IsFullMoone: business personas always do (their roles are granted only behind a
 * Full Moone Customer registration), a Customer once their version says so. Gates creating a
 * verified-only group.
 */
export const isFullMoonePersona = (
  roleCode: string | null | undefined,
  version: Version | null | undefined,
) =>
  (!!roleCode && roleCode !== AccessRoleCodes.Customer) ||
  version === Version.FullMoone;
